import { bridge } from "./core";
import {
  resolveLoginContext,
  type LoginContextOverrides,
} from "./loginContext";
import {
  AppInfo,
  AppbarInfo,
  NotificationPermissionResult,
} from "@/types/bridge";
// import { toast } from '@/components/common/Toast'
import {
  SystemShareInterface,
  CommonInterface,
  CommonFunctionInterface,
} from "@/interface/common";
import { FileInfo } from "@/types/aiFunction";

// const getFullUrl = (url: string) => {
//   // 如果是相对路径，拼接完整 URL
//   if (url.startsWith('/')) {
//     const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://test-h5-api.buscari.com'
//     return `${baseUrl}${url}`
//   }
//   return url
// }

// interface OpenWebviewParams {
//   /** 跳转的 URL 地址，支持完整 URL 或以 / 开头的路径 */
//   url: string
//   /** 新 webview navbar 中展示的标题 */
//   title?: string
//   /** 新 webview 是否为沉浸式全屏，true-全屏不显示导航栏，false-显示导航栏 */
//   immersive?: boolean
//   /** 是否为站内页面，true-会在 url 中拼接 sourcePage 参数，false-不做处理 */
//   innerLink?: boolean
//   /** 新开页面是否为全屏，true-全屏打开，false-半屏打开 */
//   isFullScreen?: boolean
//   /** 来源页面标识，用于拼接 sourcePage 参数 */
//   sourcePage?: string
// }

// 基础通信方法
export const bridgeMethod = {
  /**
   * 调用客户端方法
   * @param channel - 通信通道名称
   * @param method - 调用的方法名
   * @param params - 传递的参数
   */
  call: <T>(channel: string, method: string, params?: CommonInterface) =>
    bridge.call<T>(channel, method, params),

  /**
   * 注册方法供客户端调用
   * @param name - 注册的方法名
   * @param callback - 方法实现的回调函数
   */
  register: (name: string, callback: CommonFunctionInterface) =>
    bridge.register(name, callback),
  // 客户端不同channel ,处理不同事情 目前暂未支持
  callhandler: (
    channel: string,
    method: string,
    params: CommonInterface = {}
  ) => {
    return new Promise((resolve, reject) => {
      const data: string = JSON.stringify(
        Object.assign(params, { method: method })
      );
      window[channel]?.postMessage(data);
      window.groot[method] = (res: CommonInterface) => {
        try {
          // const data = JSON.parse(res);
          resolve(res.value);
        } catch (error) {
          console.error("bridge error:", error);
          reject(res);
        }
      };
    });
  },
};

// 业务通信方法
export const nativeMethod = {
  /**
   * 调起端内系统分享弹窗
   * @param data - 参数  title: string, text: string, icon: string, url: string
   */
  systemShare: (data: SystemShareInterface) =>
    bridge.call("quan_function", "systemShare", data),
  /**
   * 调用客户端埋点上报
   * @param data - 要发送的数据
   */
  sendTrackEvent: (data: { eventId: string; params: CommonInterface }) =>
    bridge.call("quan_function", "sendTrackEvent", data),
  /**
   * 发送数据到客户端
   * @param data - 要发送的数据
   */
  sendData: (data: CommonInterface) =>
    bridge.call("quan_function", "sendData", data),

  /**
   * 拉起登陆弹窗
   * @returns 无
   */
  openLogin: (params: LoginContextOverrides = {}) =>
    bridge.call("quan_function", "login", resolveLoginContext(params)),
  /**
   * 获取设备信息
   * @returns 设备信息对象
   */
  getDeviceInfo: () => bridge.call("quan_function", "getDeviceInfo"),

  /**
   * 获取搜索信息
   * @returns 搜索相关信息
   */
  getSearchInfo: () => bridge.call("quan_function", "getSearchInfo"),

  /**
   * 埋点上报
   * @param eventId - 事件 ID
   * @param params - 埋点参数
   */
  track: (eventId: string, params: CommonInterface) =>
    bridge.call("quan_log", "track", { eventId, params }),

  /**
   * 获取 App 信息
   * @returns App 版本、构建号等信息
   */
  getAppInfo: () => bridge.call<AppInfo>("quan_function", "getAppInfo"),

  /**
   * 获取状态栏等高度信息
   * @returns 状态栏、导航栏、底部安全区域高度
   */
  getAppbar: () => bridge.call<AppbarInfo>("quan_function", "getAppbar"),

  /**
   * 关闭当前 webview
   */
  closeWebview: () => bridge.call("quan_function", "closeWebview"),

  /**
   * 展示反馈面板
   * @param options - 反馈面板配置选项
   */
  showFeedback: (options?: {
    title?: string;
    type?: "bug" | "feature" | "other";
  }) => bridge.call("quan_function", "showFeedbackAction", options),

  /**
   * 复制内容到剪贴板
   * @param text - 要复制的文本内容
   */
  copyToClipboard: (text: string) =>
    bridge.call("quan_function", "copyToPasteboard", { text }),
  /**
   * 复制内容到剪贴板
   * @param  params - 要保存的文件信息
   */
  saveToLocal: (params: CommonInterface) =>
    bridge.call("quan_function", "saveToLocal", params),
  /**
   * 调起翻译
   * @param params - 要翻译的文件信息
   */
  showTranslate: (params: CommonInterface) =>
    bridge.call("quan_function", "showTranslate", params),
  /**
   * 调起文件预览
   * @param params - 要预览的文件信息
   */
  showFilePreview: (params: CommonInterface) =>
    bridge.call("quan_function", "showFilePreview", params),
  /**
   * 调起图片预览
   * @param params - 要预览的图片信息
   */
  showImgPreview: (params: CommonInterface) =>
    bridge.call("quan_function", "showImgPreview", params),
  /**
   * 调起文件助手历史记录
   */
  showFileAssistantHistory: () =>
    bridge.call("quan_function", "showFileAssistantHistory"),
  /**
   * 文件助手-查询正在上传的文件列表
   * @returns 状态、文件列表
   */
  queryUploadFileList: () =>
    bridge.call<FileInfo[]>("quan_function", "queryUploadFileList"),
  /**
   * 网页总结-打开vip购买页
   * @returns 状态、提示文案
   */
  showVipBuy: () => bridge.call<AppbarInfo>("quan_function", "showVipBuy"),
  /**
   * 网页总结-获取网页信息
   * @returns 状态、网页信息
   */
  queryWebsiteInfo: () =>
    bridge.call<AppbarInfo>("quan_function", "queryWebsiteInfo"),
  /**
   * 告知app页面加载成功
   * @returns 无
   */
  pageLoadSuccess: () =>
    bridge.call<AppbarInfo>("quan_function", "pageLoadSuccess"),
  /**
   * 文件助手-单文件重新上传
   * @param params - 文件信息 -id -路径
   */
  fileReupload: (params: CommonInterface) =>
    bridge.call<AppbarInfo>("quan_function", "fileReupload", params),
  /**
   * 文件助手-单文件取消上传
   * @param params - 文件信息 -id -路径
   */
  fileCancelReupload: (params: CommonInterface) =>
    bridge.call<AppbarInfo>("quan_function", "fileCancelReupload", params),
  /**
   * 文件助手-打开上传文件界面
   */
  showUpload: () => bridge.call<AppbarInfo>("quan_function", "showUpload"),
  /**
   * 文件助手-跳转扫描助手
   */
  jumpToScanAssistant: () =>
    bridge.call<AppbarInfo>("quan_function", "jumpToScanAssistant"),
  /**
   * 保存图片到相册
   * @param url - 图片 URL
   * @returns 保存结果
   */
  saveImageToAlbum: (url: string) =>
    bridge.call<{ success: boolean }>(
      "quan_function",
      "saveImageToPhotosAlbum",
      { url }
    ),

  /**
   * 显示模态框
   * @param options - 模态框配置选项
   * @param options.title - 标题
   * @param options.content - 内容
   * @param options.confirmText - 确认按钮文本
   * @param options.cancelText - 取消按钮文本（可选）
   * @returns 用户操作结果，confirm-确认，cancel-取消
   */
  showModal: (options: {
    title: string;
    content: string;
    confirmText?: string;
    cancelText?: string;
  }) =>
    bridge.call<{ action: "confirm" | "cancel" }>(
      "quan_function",
      "showModal",
      {
        title: options.title,
        content: options.content,
        confirmText: options.confirmText,
        cancelText: options.cancelText,
      }
    ),

  /**
   * 打开新的 webview
   * @param url - 跳转的 URL 地址
   * @param title - 页面标题（可选）
   */
  openWebview: (url: string, title?: string) => {
    // 如果是相对路径，使用 window.location.origin 拼接完整 URL
    const fullUrl = url.startsWith("/")
      ? `${window.location.origin}${url}`
      : url;

    return bridge.call("quan_function", "openWebview", { url: fullUrl, title });
  },
  // 调取客户端通用分享面板
  showNativeShare: (title: string, text: string, icon: string, url: string) => {
    // callhandler('groot_function', 'tiktik://jump', { title, text, icon, url });
    window.open(
      `seekee://jump/sheet/share?url=${encodeURIComponent(url)}&title=${title}&text=${encodeURIComponent(text)}&icon=${encodeURIComponent(icon)}`
    );
  },
  // 调取客户端通用分享面板2
  showNativeShare2: (
    title: string,
    text: string,
    icon: string,
    url: string
  ) => {
    // callhandler('groot_function', 'tiktik://jump', { title, text, icon, url });
    window.top.location.href = `seekee://jump/sheet/share?url=${encodeURIComponent(url)}&title=${title}&text=${encodeURIComponent(text)}&icon=${encodeURIComponent(icon)}`;
  },
  /**
   * 新闻详情页-更新话题订阅状态
   * @param params - 话题封面url -话题ID -订阅状态
   */
  updateNewsFollowStatus: (params: {
    imageUrl: string;
    topicId: string | number;
    subscribe: boolean;
  }) =>
    bridge.call<AppbarInfo>("quan_function", "updateNewsFollowStatus", params),
  /**
   * 新闻详情页-查询通知权限
   * @returns status 当前是否已开启通知权限 0-未开启 1-开启；hadPopup 冷启动后是否已弹出过引导 0-未弹 1-已弹
   */
  requestNotificationPermission: () =>
    bridge.call<NotificationPermissionResult>(
      "quan_function",
      "requestNotificationPermission"
    ),
};

const bridgeMethods = {
  ...bridgeMethod,
  ...nativeMethod,
};

export default bridgeMethods;
